import { ref, computed, onUnmounted, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import BotTestChatAPI from 'dashboard/api/omni/botTestChat';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export function useBotTestChat() {
  const messages = ref([]);
  const conversation = ref(null);
  const loading = ref(false);
  const sending = ref(false);
  const currentBotId = ref(null);
  const pollingTimer = ref(null);
  const noInboxConnected = ref(false);
  const errorMessage = ref('');
  const isFetching = ref(false);
  const messagesVersion = ref(0);
  const waitingForResponse = ref(false);

  // Tracks the message count snapshot taken right before the user sends,
  // used to decide when the bot has finished replying.
  const outgoingCountBeforeSend = ref(0);

  // Grace-period state: after detecting a new outgoing message we keep
  // polling for a few more rounds to catch all rapid-fire bot messages.
  const graceRemainingPolls = ref(0);
  const lastKnownCount = ref(0);

  const GRACE_POLLS = 6;
  const GRACE_INTERVAL = 800; // ms
  const AGGRESSIVE_INTERVAL = 600;
  const NORMAL_INTERVAL = 3000;
  const MAX_AGGRESSIVE_POLLS = 30;

  const hasMessages = computed(() => messages.value.length > 0);
  const lastMessageId = computed(() => {
    if (messages.value.length === 0) return null;
    return messages.value[messages.value.length - 1].id;
  });

  // ---- ActionCable listener ------------------------------------------------
  // Uses the KANBAN_MESSAGE_RECEIVED event that actionCable.js already emits
  // for every message.created. We filter to our conversation and trigger a
  // debounced fetch so the full message list stays accurate.

  let cableFetchTimer = null;

  const onCableMessageReceived = ({ conversationId, message }) => {
    if (!conversation.value) return;
    if (Number(conversationId) !== Number(conversation.value.id)) return;

    // Only care about outgoing (bot) messages for the real-time effect.
    // Incoming messages are added optimistically in sendMessage().
    if (message?.message_type === 0 || message?.message_type === 'outgoing') {
      scheduleFetchFromCable();
    }
  };

  const scheduleFetchFromCable = () => {
    if (cableFetchTimer) clearTimeout(cableFetchTimer);
    // Small debounce: when the bot sends multiple messages at once
    // we batch them into a single fetch.
    cableFetchTimer = setTimeout(async () => {
      cableFetchTimer = null;
      await fetchMessages();
    }, 300);
  };

  const subscribeToCable = () => {
    emitter.on(BUS_EVENTS.KANBAN_MESSAGE_RECEIVED, onCableMessageReceived);
  };

  const unsubscribeFromCable = () => {
    emitter.off(BUS_EVENTS.KANBAN_MESSAGE_RECEIVED, onCableMessageReceived);
    if (cableFetchTimer) {
      clearTimeout(cableFetchTimer);
      cableFetchTimer = null;
    }
  };

  // ---- Init / fetch --------------------------------------------------------

  const initChat = async botId => {
    if (!botId) return;

    currentBotId.value = botId;
    loading.value = true;
    noInboxConnected.value = false;
    errorMessage.value = '';

    try {
      const { data } = await BotTestChatAPI.getConversation(botId);
      conversation.value = data.conversation;
      messages.value = data.messages || [];
      messagesVersion.value++;

      if (conversation.value) {
        subscribeToCable();
        startPolling();
      }
    } catch (error) {
      if (error.response?.status === 422) {
        noInboxConnected.value = true;
        errorMessage.value =
          error.response?.data?.error ||
          'Bot não está conectado a uma inbox. Conecte-o primeiro.';
      } else {
        useAlert('Erro ao inicializar chat de teste');
      }
    } finally {
      loading.value = false;
    }
  };

  const fetchMessages = async () => {
    if (!currentBotId.value || !conversation.value) return;
    if (isFetching.value) return;

    isFetching.value = true;

    try {
      const { data } = await BotTestChatAPI.getMessages(currentBotId.value, {
        conversation_id: conversation.value.id,
      });

      const newMessages = data.messages || [];
      const hasChanges = mergeMessages(newMessages);

      if (hasChanges) {
        messagesVersion.value++;
      }

      noInboxConnected.value = false;
    } catch (error) {
      if (error.response?.status === 422) {
        noInboxConnected.value = true;
        errorMessage.value =
          error.response?.data?.error ||
          'Bot não está conectado a uma inbox. Conecte-o primeiro.';
        stopPolling();
      }
    } finally {
      isFetching.value = false;
    }
  };

  // Smart merge: keeps pending (optimistic) messages while incorporating
  // server data and deduplicating.
  const mergeMessages = newMessages => {
    const currentIds = new Set(
      messages.value.filter(m => !String(m.id).startsWith('temp_')).map(m => m.id)
    );

    const pendingMessages = messages.value.filter(m =>
      String(m.id).startsWith('temp_')
    );

    const hasNewMessages = newMessages.some(m => !currentIds.has(m.id));
    const pendingConfirmed = pendingMessages.some(pending =>
      newMessages.some(
        m => m.content === pending.content && m.message_type === pending.message_type
      )
    );

    if (
      !hasNewMessages &&
      !pendingConfirmed &&
      messages.value.length === newMessages.length
    ) {
      return false;
    }

    const confirmedPendingIds = new Set();
    pendingMessages.forEach(pending => {
      const confirmed = newMessages.find(
        m => m.content === pending.content && m.message_type === pending.message_type
      );
      if (confirmed) confirmedPendingIds.add(pending.id);
    });

    const remainingPending = pendingMessages.filter(
      m => !confirmedPendingIds.has(m.id)
    );

    messages.value = [...newMessages, ...remainingPending];
    return true;
  };

  // ---- Send ----------------------------------------------------------------

  const sendMessage = async content => {
    if (!currentBotId.value || !content?.trim()) return;

    sending.value = true;

    const tempId = `temp_${Date.now()}`;
    const tempMessage = {
      id: tempId,
      content: content.trim(),
      message_type: 'incoming',
      created_at: new Date().toISOString(),
      sender_name: 'Você',
      pending: true,
    };
    messages.value = [...messages.value, tempMessage];
    messagesVersion.value++;

    try {
      const { data } = await BotTestChatAPI.sendMessage(
        currentBotId.value,
        content.trim()
      );

      // Replace temp message with the real one
      const index = messages.value.findIndex(m => m.id === tempId);
      if (index !== -1) {
        const updated = [...messages.value];
        updated[index] = data.message;
        messages.value = updated;
      }

      // Start aggressive polling to pick up bot replies
      outgoingCountBeforeSend.value = countOutgoing();
      lastKnownCount.value = outgoingCountBeforeSend.value;
      graceRemainingPolls.value = 0;
      waitingForResponse.value = true;
      startPolling(true);
    } catch (error) {
      useAlert(
        'Erro ao enviar mensagem: ' +
          (error.response?.data?.error || error.message)
      );
      messages.value = messages.value.filter(m => m.id !== tempId);
    } finally {
      sending.value = false;
    }
  };

  // ---- Reset ---------------------------------------------------------------

  const resetChat = async (clearHistory = false) => {
    if (!currentBotId.value) return;

    sending.value = true;

    try {
      const { data } = await BotTestChatAPI.reset(
        currentBotId.value,
        clearHistory
      );

      // The backend always (re)creates the conversation for the bot's current
      // inbox during reset. Sync local state so we don't keep referencing a
      // stale conversation from a previous inbox.
      if (data?.conversation) {
        const previousId = conversation.value?.id;
        conversation.value = data.conversation;
        if (!previousId) {
          subscribeToCable();
        }
      }

      if (clearHistory) {
        messages.value = [];
        messagesVersion.value++;
        useAlert('Conversa limpa e #reset enviado');
      } else {
        useAlert('#reset enviado');
      }

      await fetchMessages();
      outgoingCountBeforeSend.value = countOutgoing();
      lastKnownCount.value = outgoingCountBeforeSend.value;
      graceRemainingPolls.value = 0;
      waitingForResponse.value = true;
      startPolling(true);
    } catch (error) {
      // If the bot is no longer connected to any inbox we surface the proper
      // empty state instead of a generic error.
      if (error.response?.status === 422) {
        noInboxConnected.value = true;
        errorMessage.value =
          error.response?.data?.error ||
          'Bot não está conectado a uma inbox. Conecte-o primeiro.';
        conversation.value = null;
        messages.value = [];
        messagesVersion.value++;
        stopPolling();
      } else {
        useAlert('Erro ao resetar conversa');
      }
    } finally {
      sending.value = false;
    }
  };

  // ---- Polling -------------------------------------------------------------
  // The poller now implements a "grace period": after the first new outgoing
  // message is detected it keeps polling for GRACE_POLLS more rounds. If
  // additional messages arrive during the grace period the counter resets,
  // ensuring we always wait until the bot is truly done sending.

  const countOutgoing = () =>
    messages.value.filter(
      m => m.message_type === 'outgoing' && !String(m.id).startsWith('temp_')
    ).length;

  const startPolling = (aggressive = false) => {
    stopPolling();

    let pollCount = 0;
    const maxPolls = aggressive ? MAX_AGGRESSIVE_POLLS : 15;

    const poll = async () => {
      pollCount++;
      await fetchMessages();

      const currentOutgoing = countOutgoing();

      if (aggressive && waitingForResponse.value) {
        // New outgoing messages arrived since last check?
        if (currentOutgoing > lastKnownCount.value) {
          // Reset grace period — more messages may follow
          lastKnownCount.value = currentOutgoing;
          graceRemainingPolls.value = GRACE_POLLS;
        }

        if (graceRemainingPolls.value > 0) {
          // Inside grace period: keep polling at grace interval
          graceRemainingPolls.value--;
          pollingTimer.value = setTimeout(poll, GRACE_INTERVAL);
          return;
        }

        // Grace period finished (or never started yet)
        if (currentOutgoing > outgoingCountBeforeSend.value) {
          // Bot has replied and no new messages for GRACE_POLLS rounds — done
          waitingForResponse.value = false;
          startPolling(false); // switch to background polling
          return;
        }
      }

      if (pollCount >= maxPolls) {
        waitingForResponse.value = false;
        stopPolling();
        return;
      }

      const interval = aggressive && pollCount <= 10
        ? AGGRESSIVE_INTERVAL
        : NORMAL_INTERVAL;
      pollingTimer.value = setTimeout(poll, interval);
    };

    poll();
  };

  const stopPolling = () => {
    if (pollingTimer.value) {
      clearTimeout(pollingTimer.value);
      pollingTimer.value = null;
    }
  };

  // ---- Refresh / cleanup ---------------------------------------------------

  const refresh = async () => {
    await fetchMessages();
    startPolling();
  };

  const changeBotId = async botId => {
    stopPolling();
    unsubscribeFromCable();
    messages.value = [];
    conversation.value = null;
    noInboxConnected.value = false;
    errorMessage.value = '';
    messagesVersion.value = 0;
    await initChat(botId);
  };

  onUnmounted(() => {
    stopPolling();
    unsubscribeFromCable();
  });

  return {
    messages,
    conversation,
    loading,
    sending,
    currentBotId,
    noInboxConnected,
    errorMessage,
    messagesVersion,
    waitingForResponse,

    hasMessages,
    lastMessageId,

    initChat,
    fetchMessages,
    sendMessage,
    resetChat,
    changeBotId,
    startPolling,
    stopPolling,
    refresh,
  };
}
